import React, { useCallback, useState } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import update from 'immutability-helper';
import {
  Button,
  DEFAULT_THEME,
  Divider,
  Group,
  MediaQuery,
  Space,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import Image from 'next/image';
import { CloudinaryImage } from '@cloudinary/url-gen';
import { AdvancedImage } from '@cloudinary/react';
import { name as transformationName } from '@cloudinary/url-gen/actions/namedTransformation';
import { IconDownload, IconGripVertical, IconVolume } from '@tabler/icons';
import { useMediaQuery } from '@mantine/hooks';
import { cld } from '../../src/Utils';
import { TIER_COLOR_SWATCH } from '../GradientPicker/GradientPicker';

export interface ContainerState {
  cards: any[];
}

export interface SongData {
  id: number;
  name: string;
  artist: string;
  songLink: string;
  thumbnail: CloudinaryImage;
}

interface Item {
  id: string;
  originalIndex: number;
}

export interface SongCardData {
  id: string;
  songData: SongData;
  moveCard: (id: string, atIndex: number) => void;
  findCard: (id: string) => { card: SongData; index: number };
  onListen: (song: SongData) => void;
}

const pushups = cld
  .image('drake-pushups-spotify-v0-LJeYC0fMNj5Dr0Yc0XFhYawCm2iBDZAl1_l4lHyWYkc_usacki')
  .namedTransformation(transformationName('tier-list-image-4x'));

const euphoria = cld
  .image('25E2_2580_259Ceuphoria_25E2_2580_259D_wclcdk')
  .namedTransformation(transformationName('tier-list-image-4x'));

const taylorMade = cld
  .image('artworks-na8VFewpiRq9DpnP-JpscqA-t500x500_cnof1j')
  .namedTransformation(transformationName('tier-list-image-4x'));

const sixinla = cld
  .image('220px-Kendrick_Lamar_-_6-16_in_LA_x3o95y')
  .namedTransformation(transformationName('tier-list-image-4x'));
const familymatters = cld
  .image('artworks-iGrxKHOg1q5I-0-t500x500_a4vvfc')
  .namedTransformation(transformationName('tier-list-image-4x'));
const notlikeus = cld
  .image('kendrick-lamar-not-like-us-Cover-Art_oojl3g')
  .namedTransformation(transformationName('tier-list-image-4x'));
const meetgrahams = cld
  .image('meet-the-grahams-cover-art-discussion-v0-bms5w51ogcyc1_kh2nb3')
  .namedTransformation(transformationName('tier-list-image-4x'));
const heart6 = cld
  .image('099d2c8a1efd293e916b2889ed66eabf.1000x1000x1_pmtrf8')
  .namedTransformation(transformationName('tier-list-image-4x'));

export const SONG_DATA: SongData[] = [
  {
    id: 0,
    name: 'Pushups',
    thumbnail: pushups,
    artist: 'Drake',
    songLink: 'https://www.youtube.com/embed/HKH9p19PRLA?si=EPe1iuvQtgfrzw6O',
  },
  {
    id: 1,
    name: 'Taylor Made Freestyle',
    thumbnail: taylorMade,
    artist: 'Drake',
    songLink: 'https://www.youtube.com/embed/zhEiduBxX4c?si=PlezRnvf5NHPOqOq',
  },
  {
    id: 2,
    name: 'Euphoria',
    thumbnail: euphoria,
    artist: 'Kendrick Lamar',
    songLink: 'https://www.youtube.com/embed/NPqDIwWMtxg?si=Q_E_DNJEEiTlThk0',
  },
  {
    id: 3,
    name: '6:16 in LA',
    thumbnail: sixinla,
    artist: 'Kendrick Lamar',
    songLink: 'https://www.youtube.com/embed/MabjUIUj6uo?si=4g_S5kvSsM4XN2SP',
  },
  {
    id: 4,
    name: 'Family Matters',
    thumbnail: familymatters,
    artist: 'Drake',
    songLink: 'https://www.youtube.com/embed/ZkXG3ZrXlbc?si=wGGcm9n5NoR99v2u',
  },
  {
    id: 5,
    name: 'Meet The Grahams',
    thumbnail: meetgrahams,
    artist: 'Kendrick Lamar',
    songLink: 'https://www.youtube.com/embed/2QiFl9Dc7D0?si=L3uRHz6tMg02n5uf',
  },
  {
    id: 6,
    name: 'Not Like Us',
    thumbnail: notlikeus,
    artist: 'Kendrick Lamar',
    songLink: 'https://www.youtube.com/embed/phLb_SoPBlA?si=E_EH4hZmNHWYIebh',
  },
  {
    id: 7,
    name: 'The Heart Part 6',
    thumbnail: heart6,
    artist: 'Drake',
    songLink: 'https://www.youtube.com/embed/HJeY-FXidDQ?si=KPMOVN7NLPyx-f3L',
  },
];

const SongCard = ({ id, songData, moveCard, findCard, onListen }: SongCardData) => {
  const originalIndex = findCard(id).index;
  const [{ isDragging, target }, drag, preview] = useDrag(
    () => ({
      type: 'song',
      item: { id, originalIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        target: monitor.isDragging() ? monitor.getItem().originalIndex : -1,
      }),
      end: (item, monitor) => {
        const { id: droppedId, originalIndex: ogIdx } = item;
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          moveCard(droppedId, ogIdx);
        }
      },
    }),
    [id, originalIndex, moveCard]
  );

  console.log(target);

  const [, drop] = useDrop(
    () => ({
      accept: 'song',
      hover({ id: draggedId }: Item) {
        if (draggedId !== id) {
          const { index: overIndex } = findCard(id);
          moveCard(draggedId, overIndex);
        }
      },
    }),
    [findCard, moveCard]
  );

  const opacity = isDragging ? 0 : 1;

  const previewOpacity = isDragging ? 1 : 0;

  const theme = useMantineTheme();

  const rowHeight = 65;
  const mobileRowHeight = 40;

  const isSmall = useMediaQuery(`(max-width: 600px)`);

  return (
    <>
      <Group
        ref={(node) => preview(drop(node))}
        style={{
          opacity,
          border: `2px solid ${theme.colors.dark[5]}`,
          borderRadius: 8,
        }}
        my={5}
        maw={1000}
      >
        <>
          {!isSmall && (
            <Group
              style={{
                width: '100%',
              }}
              p="md"
            >
              <div ref={drag}>
                <IconGripVertical size={40} />
              </div>
              <Stack>
                <Group
                  align="center"
                  style={{
                    justifyContent: 'center',
                    borderRadius: 8,
                  }}
                  bg={TIER_COLOR_SWATCH[originalIndex]}
                  w={rowHeight}
                  h={rowHeight}
                >
                  <Text size={30} weight="bolder" color="white">
                    {' '}
                    {originalIndex + 1}
                  </Text>
                </Group>
              </Stack>
              <Divider mx="sm" orientation="vertical" size="3" />
              <AdvancedImage
                alt={`${songData.name} cover art`}
                cldImg={songData.thumbnail}
                width={rowHeight}
                height={rowHeight}
                style={{
                  borderRadius: 6,
                }}
              />
              <Stack
                style={{
                  gap: 5,
                }}
              >
                <Text weight="bold" size="xl" lh={1}>
                  {songData.name}
                </Text>
                <Text
                  size="md"
                  weight={500}
                  color={songData.artist === 'Drake' ? '#B197FC' : DEFAULT_THEME.colors.pink[6]}
                >
                  {songData.artist}
                </Text>
              </Stack>
              <Space
                style={{
                  flex: 1,
                }}
              />
              <Divider mx="md" orientation="vertical" size="3" />
              <Stack w={125}>
                <Button
                  size="md"
                  variant="light"
                  rightIcon={<IconVolume size={22} />}
                  onClick={() => {
                    onListen(songData);
                  }}
                >
                  Listen
                </Button>
              </Stack>
            </Group>
          )}
          {isSmall && (
            <Group
              style={{
                width: 500,
                gap: 0,
              }}
              noWrap
              py="sm"
              px={5}
            >
              <div
                ref={drag}
                style={{
                  width: 40,
                  height: 40,
                  justifyContent: 'center',
                  alignItems: 'center',
                  left: -5,
                }}
              >
                <IconGripVertical size={40} />
              </div>
              <Stack mr="xs">
                <Group
                  align="center"
                  style={{
                    justifyContent: 'center',
                    borderRadius: 8,
                  }}
                  bg={TIER_COLOR_SWATCH[originalIndex]}
                  w={mobileRowHeight}
                  h={mobileRowHeight}
                >
                  <Text size={20} weight="bolder" color="white">
                    {' '}
                    {originalIndex + 1}
                  </Text>
                </Group>
              </Stack>
              {/* <Divider mx="xs" orientation="vertical" size="3" /> */}
              <AdvancedImage
                alt={`${songData.name} cover art`}
                cldImg={songData.thumbnail}
                width={mobileRowHeight}
                height={mobileRowHeight}
                style={{
                  borderRadius: 6,
                }}
              />
              <Stack
                style={{
                  gap: 3,
                  maxWidth: 200,
                }}
                mx="xs"
              >
                <Text weight="bold" size="sm" lineClamp={2} lh={1}>
                  {songData.name}
                </Text>
                <Text
                  size="xs"
                  weight={500}
                  color={songData.artist === 'Drake' ? '#B197FC' : DEFAULT_THEME.colors.pink[6]}
                >
                  {songData.artist}
                </Text>
              </Stack>
              <Space
                style={{
                  flex: 1,
                }}
              />
              {/* <Divider mx="md" orientation="vertical" size="3" /> */}
              <Stack>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    onListen(songData);
                  }}
                >
                  Listen
                </Button>
              </Stack>
            </Group>
          )}
        </>
      </Group>
    </>
  );
};

const SongGrid = ({
  onListen,
  songs,
  setSongs,
}: {
  onListen: (song: SongData) => void;
  songs: SongData[];
  setSongs: (songs: SongData[]) => void;
}) => {
  const [, drop] = useDrop(() => ({ accept: 'song' }));

  const findCard = useCallback(
    (id: string) => {
      const card = songs.filter((c) => `${c.id}` === id)[0];
      return {
        card,
        index: songs.indexOf(card),
      };
    },
    [songs]
  );

  const moveCard = useCallback(
    (id: string, atIndex: number) => {
      const { card, index } = findCard(id);
      setSongs(
        update(songs, {
          $splice: [
            [index, 1],
            [atIndex, 0, card],
          ],
        })
      );
    },
    [findCard, songs, setSongs]
  );

  return (
    <Stack
      ref={drop}
      style={{
        gap: 0,
      }}
    >
      {songs.map((song) => (
        <SongCard
          key={song.id}
          id={`${song.id}`}
          songData={song}
          moveCard={moveCard}
          findCard={findCard}
          onListen={() => onListen(song)}
        />
      ))}
    </Stack>
  );
};

export default SongGrid;
